import { Query } from 'best-modules/utils'
import { sendRequest } from 'best-modules/vue-utils'
import {
  Audit,
  AuditDTO,
  AuditFlaw,
  AuditObjectDepartment,
  AuditRecommendation,
  AuditRisk,
} from '@/utils/types/audit'
import { PaginationList } from 'best-modules/components/server-table/types'
import { ISODate } from 'best-modules/utils/customDate'

const auditList = (query: Query): Promise<PaginationList<Audit>> => {
  return sendRequest({
    name: 'auditList',
    method: 'get',
    url: {
      url: '/json/audit',
      query,
    },
  })
}

const auditSingle = (auditId: number): Promise<Audit> => {
  return sendRequest({
    name: 'auditSingle',
    method: 'get',
    url: `/json/audit/single/${auditId}`,
  })
}

const auditCreate = (credentials: AuditDTO): Promise<Audit> => {
  return sendRequest({
    name: 'auditCreate',
    method: 'post',
    url: '/json/audit/create',
    payload: credentials,
  })
}

const auditUpdate = (
  auditId: number,
  credentials: AuditDTO
): Promise<Audit> => {
  return sendRequest({
    name: 'auditUpdate',
    method: 'post',
    url: `/json/audit/update/${auditId}`,
    payload: credentials,
  })
}

const auditDocumentUpdate = (
  auditId: number,
  credentials: Partial<
    Pick<
      AuditDTO,
      'auditProgram' | 'auditReport' | 'auditAnnouncement' | 'otherDocuments'
    >
  >
): Promise<Audit> => {
  return sendRequest({
    name: 'auditDocumentUpdate',
    method: 'post',
    url: `/json/audit/document/${auditId}`,
    payload: credentials,
  })
}

const auditTakeToWork = (auditId: number): Promise<Audit> => {
  return sendRequest({
    name: 'auditTakeToWork',
    method: 'get',
    url: `/json/audit/inWork/${auditId}`,
  })
}

const auditComplete = (
  auditId: number,
  internalControlAssessmentId: number
): Promise<Audit> => {
  return sendRequest({
    name: 'auditComplete',
    method: 'post',
    url: `/json/audit/completed/${auditId}`,
    payload: {
      auditId,
      internalControlAssessmentId,
    },
  })
}

const auditFlawCreate = (credentials: {
  auditId: number
  text: string
  factorFlaw: string
}): Promise<AuditFlaw> => {
  return sendRequest({
    name: 'auditFlawCreate',
    method: 'post',
    url: '/json/audit/flaw/create',
    payload: credentials,
  })
}

const auditFlawUpdate = (
  flawId: number,
  credentials: {
    auditId: number
    text: string
    factorFlaw: string
  }
): Promise<AuditFlaw> => {
  return sendRequest({
    name: 'auditFlawUpdate',
    method: 'post',
    url: `/json/audit/flaw/update/${flawId}`,
    payload: credentials,
  })
}

const auditFlawDelete = (flawId: number): Promise<AuditFlaw> => {
  return sendRequest({
    name: 'auditFlawDelete',
    method: 'delete',
    url: `/json/audit/flaw/delete/${flawId}`,
  })
}

const auditRecommendationCreate = (credentials: {
  auditFlawId: number
  topic: string
  executorId: number
  deadline: ISODate
  description: string
}): Promise<AuditRecommendation> => {
  return sendRequest({
    name: 'auditRecommendationCreate',
    method: 'post',
    url: '/json/audit/recommendation/create',
    payload: credentials,
  })
}

type AuditRecommendationExportResponse = {
  url: string
}

const auditRecommendationExport = (): Promise<
  AuditRecommendationExportResponse
> => {
  return sendRequest({
    name: 'auditRecommendationExport',
    method: 'get',
    url: '/json/audit/recommendation/export',
  })
}

const auditRecommendationSetStatus = (
  recommendationId: number,
  statusId: number
): Promise<AuditRecommendation> => {
  return sendRequest({
    name: 'auditRecommendationSetStatus',
    method: 'post',
    url: `/json/audit/recommendation/setStatus/${recommendationId}`,
    payload: {
      statusId,
    },
  })
}

const auditRiskCreate = (credentials: {
  riskTypeId: number
  riskLevelId: number
  auditFlawId: number
}): Promise<AuditRisk> => {
  return sendRequest({
    name: 'auditRiskCreate',
    method: 'post',
    url: '/json/audit/flaw/risk/create',
    payload: credentials,
  })
}

const auditRiskDelete = (riskId: number): Promise<AuditRisk> => {
  return sendRequest({
    name: 'auditRiskDelete',
    method: 'delete',
    url: `/json/audit/flaw/risk/delete/${riskId}`,
  })
}

const auditObjectDepartmentList = (
  auditId: number
): Promise<AuditObjectDepartment[]> => {
  return sendRequest({
    name: 'auditObjectDepartmentList',
    method: 'get',
    url: `/json/audit/objectDepartment/byAudit/${auditId}`,
  })
}

const auditObjectDepartmentCreate = (credentials: {
  auditId: number
  internalDepartmentId: number
}): Promise<AuditObjectDepartment> => {
  return sendRequest({
    name: 'auditObjectDepartmentCreate',
    method: 'post',
    url: '/json/audit/objectDepartment/create',
    payload: credentials,
  })
}

const auditObjectDepartmentDelete = (
  objectDepartmentId: number
): Promise<void> => {
  return sendRequest({
    name: 'auditObjectDepartmentDelete',
    method: 'delete',
    url: `/json/audit/objectDepartment/delete/${objectDepartmentId}`,
  })
}

export {
  auditList,
  auditSingle,
  auditCreate,
  auditUpdate,
  auditDocumentUpdate,
  auditTakeToWork,
  auditComplete,
  auditFlawCreate,
  auditFlawUpdate,
  auditFlawDelete,
  auditRecommendationCreate,
  auditRecommendationExport,
  auditRecommendationSetStatus,
  auditRiskCreate,
  auditRiskDelete,
  auditObjectDepartmentList,
  auditObjectDepartmentCreate,
  auditObjectDepartmentDelete,
}
